import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Notes() {
  const [filter, setFilter] = useState("all"); // all | debit | credit
  const [source, setSource] = useState("all"); // all | sales | purchase
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (filter !== "all") params.note_type = filter;
        if (source !== "all") params.source_type = source;
        const res = await axios.get(`${API}/notes`, { params });
        if (!cancelled) setNotes(res.data || []);
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [filter, source]);

  const visible = notes.filter(n =>
    !q ||
    (n.note_no || "").toLowerCase().includes(q.toLowerCase()) ||
    (n.ref_invoice_no || "").toLowerCase().includes(q.toLowerCase()) ||
    (n.ref_purchase_no || "").toLowerCase().includes(q.toLowerCase()) ||
    (n.customer_name || n.supplier_name || "").toLowerCase().includes(q.toLowerCase())
  );

  const handleDownload = async (n) => {
    try {
      const res = await axios.get(`${API}/notes/${n.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${n.note_no}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert("Download failed: " + (e.response?.data?.detail || e.message)); }
  };

  const handlePrint = async (n) => {
    try {
      const res = await axios.get(`${API}/notes/${n.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const w = window.open(url); if (w) setTimeout(() => w.print(), 800);
    } catch (e) { alert("Print failed: " + (e.response?.data?.detail || e.message)); }
  };

  const handleDelete = async (n) => {
    if (!window.confirm(`Delete ${n.note_no}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/notes/${n.id}`);
      setNotes(notes.filter(x => x.id !== n.id));
    } catch (e) { alert("Delete failed: " + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="p-6" data-testid="notes-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-900">Debit &amp; Credit Notes</h1>
        <p className="text-xs text-slate-500">Notes are auto-linked to GSTR-1 — credit notes <b>reduce</b>, debit notes <b>increase</b> taxable value.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[
          { v: "all", lbl: "All" },
          { v: "credit", lbl: "Credit Notes" },
          { v: "debit", lbl: "Debit Notes" },
        ].map(f => (
          <button
            key={f.v}
            type="button"
            data-testid={`notes-filter-${f.v}`}
            onClick={() => setFilter(f.v)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-sm border ${filter === f.v ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
          >{f.lbl}</button>
        ))}
        <span className="mx-2 h-5 w-px bg-slate-200" aria-hidden />
        {[
          { v: "all", lbl: "All Sources" },
          { v: "sales", lbl: "Sales Notes" },
          { v: "purchase", lbl: "Purchase Notes" },
        ].map(s => (
          <button
            key={s.v}
            type="button"
            data-testid={`notes-source-${s.v}`}
            onClick={() => setSource(s.v)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-sm border ${source === s.v ? "bg-purple-600 text-white border-purple-600" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
          >{s.lbl}</button>
        ))}
        <Input
          placeholder="Search note no / ref / party"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="ml-auto max-w-sm"
          data-testid="notes-search"
        />
      </div>

      <p className="text-xs text-slate-500 mb-4">
        Sales notes: open any invoice → &quot;Credit/Debit Note&quot;. Purchase notes: open a purchase row → &quot;+CN&quot; / &quot;+DN&quot;.
      </p>

      <div className="bg-white border border-slate-200 rounded-sm sticky-table">
        <table className="w-full text-sm">
          <thead className="sticky-thead bg-slate-50 text-slate-700 text-xs uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Source</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Note No</th>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Ref Doc</th>
              <th className="px-4 py-3 text-left">Party</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="px-4 py-6 text-center text-slate-400">Loading…</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan="8" className="px-4 py-6 text-center text-slate-400">No notes yet. Open an invoice or purchase to create one.</td></tr>
            ) : visible.map(n => {
              const isPurchase = n.source_type === "purchase";
              const partyName = isPurchase ? (n.supplier_name || "-") : (n.customer_name || "-");
              const refNo = isPurchase ? (n.ref_purchase_no || "-") : (n.ref_invoice_no || "-");
              const editPath = isPurchase ? `/purchase-notes/${n.id}/edit` : `/notes/${n.id}/edit`;
              return (
                <tr key={n.id} data-testid={`note-row-${n.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm ${isPurchase ? "bg-orange-50 text-orange-700 border border-orange-200" : "bg-blue-50 text-blue-700 border border-blue-200"}`}>
                      {isPurchase ? "Purchase" : "Sales"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold px-2 py-1 rounded-sm ${n.note_type === "credit" ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                      {n.note_type === "credit" ? "CREDIT" : "DEBIT"}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold">{n.note_no}</td>
                  <td className="px-4 py-3 text-slate-600">{n.note_date}</td>
                  <td className="px-4 py-3 text-blue-700 font-mono text-xs">{refNo}</td>
                  <td className="px-4 py-3">{partyName}</td>
                  <td className="px-4 py-3 text-right font-semibold">{"\u20B9"}{Number(n.total || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                    {editPath && <Link to={editPath} data-testid={`edit-${n.id}`} className="text-xs text-blue-700 font-semibold">Edit</Link>}
                    <button type="button" data-testid={`download-${n.id}`} onClick={() => handleDownload(n)} className="text-xs text-slate-700 font-semibold">Download</button>
                    <button type="button" data-testid={`print-${n.id}`} onClick={() => handlePrint(n)} className="text-xs text-slate-700 font-semibold">Print</button>
                    <button type="button" data-testid={`delete-${n.id}`} onClick={() => handleDelete(n)} className="text-xs text-red-600 font-semibold">Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
